import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Upload, 
  FileText, 
  MessageSquare, 
  Users, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  BarChart3,
  PlusCircle,
  TrendingUp,
  DollarSign,
  Package,
  Wallet,
  PieChart,
  Activity,
  Banknote,
  Factory,
  FlaskConical,
  Warehouse,
  PackageOpen,
  ShoppingCart,
  Receipt,
  LineChart,
  Sun,
  Ship,
  Crosshair,
  Columns,
  MapPin,
  Database,
  CandlestickChart,
  Globe,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const API_URL = "";

// Icon mapping for different KPI types
const getKPIIcon = (name) => {
  const lower = name.toLowerCase();
  if (lower.includes('p&l') || lower.includes('profit')) return TrendingUp;
  if (lower.includes('production') || lower.includes('throughput')) return Factory;
  if (lower.includes('invoice') || lower.includes('receipt')) return Receipt;
  if (lower.includes('supplier') || lower.includes('payment')) return ShoppingCart;
  if (lower.includes('export') || lower.includes('market')) return PieChart;
  if (lower.includes('skm') || lower.includes('meat') || lower.includes('rate')) return DollarSign;
  if (lower.includes('working capital') || lower.includes('cycle')) return Activity;
  if (lower.includes('balance sheet') || lower.includes('distribution')) return Wallet;
  if (lower.includes('debt') || lower.includes('equity')) return Banknote;
  if (lower.includes('labour') || lower.includes('wage') || lower.includes('cost')) return Users;
  if (lower.includes('inventory') || lower.includes('rm')) return Package;
  return BarChart3;
};

const Sidebar = ({ collapsed, setCollapsed }) => {
  const { user, organization, logout, isAdmin, isProcurement, isStores } = useAuth();
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [dashboardsExpanded, setDashboardsExpanded] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboards();
  }, []);

  const fetchDashboards = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboards?published_only=true`, {
        withCredentials: true
      });
      setDashboards(response.data);
    } catch (e) {
      console.error('Error fetching dashboards:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const adminNavItems = [
    { to: '/builder', icon: PlusCircle, label: 'Create Dashboard', adminOnly: true },
    { to: '/dashboard', icon: LayoutDashboard, label: 'Overview', adminOnly: true },
    { to: '/files', icon: FileText, label: 'Files', adminOnly: true },
    { to: '/upload', icon: Upload, label: 'Upload', adminOnly: true },
    { to: '/admin/data', icon: Database, label: 'Data Ops', adminOnly: true },
    { to: '/kapl/entry', icon: Warehouse, label: 'KAPL Manual Entry', adminOnly: true },
    { to: '/packing-store/entry', icon: Package, label: 'Packing Store Manual Entry', adminOnly: true },
    { to: '/feed-plant/entry', icon: Factory, label: 'Feed Plant Manual Entry', adminOnly: true },
    { to: '/chat', icon: MessageSquare, label: 'AI Chat', adminOnly: true },
    { to: '/users', icon: Users, label: 'Users', adminOnly: true },
    { to: '/settings', icon: Settings, label: 'Settings', adminOnly: true },
  ];

  const filteredAdminItems = adminNavItems.filter(item => isAdmin);

  // Procurement users see a minimal 2-item sidebar (Rates upload + Price Trend)
  if (isProcurement) {
    return (
      <aside
        className={cn(
          "h-screen bg-dash-bg border-r border-dash-line flex flex-col transition-all duration-300 flex-shrink-0 relative",
          collapsed ? "w-16" : "w-72"
        )}
        data-testid="sidebar-procurement"
      >
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 z-50 w-6 h-6 rounded-full bg-dash-panel border border-dash-line flex items-center justify-center text-dash-mut hover:text-dash-ink hover:bg-dash-panel2 transition shadow-md cursor-pointer"
          style={{ top: '18px' }}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
        <div className="p-4 border-b border-dash-line">
          {!collapsed ? (
            <span className="font-semibold text-dash-ink">{organization?.name || 'SIL Insights'}</span>
          ) : (
            <div className="w-8 h-8 mx-auto rounded-lg flex items-center justify-center text-dash-ink text-sm font-bold bg-gradient-to-br from-dash-coral to-dash-ice">
              {organization?.name?.charAt(0) || 'S'}
            </div>
          )}
        </div>
        <nav className="flex-1 p-2 space-y-1">
          <NavLink to="/procurement/rates" data-testid="nav-procurement-rates"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <Upload className="w-4 h-4" />
            {!collapsed && <span>Daily Rates Upload</span>}
          </NavLink>
          <NavLink to="/price-trend" data-testid="nav-procurement-price-trend"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <TrendingUp className="w-4 h-4" />
            {!collapsed && <span>Price Trend</span>}
          </NavLink>
        </nav>
        <div className="p-3 border-t border-dash-line">
          {!collapsed && <div className="text-xs text-dash-mut mb-2 truncate" title={user?.email}>{user?.email}</div>}
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} className="w-full justify-start" data-testid="proc-logout">
            <LogOut className="w-4 h-4 mr-2" /> {!collapsed && 'Sign out'}
          </Button>
        </div>
      </aside>
    );
  }

  // Stores users see a minimal 3-item sidebar (MRO upload + log entries directly +
  // General Stores dashboard), mirroring the Procurement layout exactly.
  if (isStores) {
    return (
      <aside
        className={cn(
          "h-screen bg-dash-bg border-r border-dash-line flex flex-col transition-all duration-300 flex-shrink-0 relative",
          collapsed ? "w-16" : "w-72"
        )}
        data-testid="sidebar-stores"
      >
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 z-50 w-6 h-6 rounded-full bg-dash-panel border border-dash-line flex items-center justify-center text-dash-mut hover:text-dash-ink hover:bg-dash-panel2 transition shadow-md cursor-pointer"
          style={{ top: '18px' }}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
        <div className="p-4 border-b border-dash-line">
          {!collapsed ? (
            <span className="font-semibold text-dash-ink">{organization?.name || 'SIL Insights'}</span>
          ) : (
            <div className="w-8 h-8 mx-auto rounded-lg flex items-center justify-center text-dash-ink text-sm font-bold bg-gradient-to-br from-dash-coral to-dash-ice">
              {organization?.name?.charAt(0) || 'S'}
            </div>
          )}
        </div>
        <nav className="flex-1 p-2 space-y-1">
          <NavLink to="/stores/entry" data-testid="nav-stores-entry"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <PlusCircle className="w-4 h-4" />
            {!collapsed && <span>Log Stock Entry</span>}
          </NavLink>
          <NavLink to="/general-stores" data-testid="nav-stores-general-stores"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <Package className="w-4 h-4" />
            {!collapsed && <span>General Stores</span>}
          </NavLink>
          <NavLink to="/feed-plant/entry" data-testid="nav-stores-feed-plant-entry"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <PlusCircle className="w-4 h-4" />
            {!collapsed && <span>Log Feed Plant Entry</span>}
          </NavLink>
          <NavLink to="/feed-plant" data-testid="nav-stores-feed-plant"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <Factory className="w-4 h-4" />
            {!collapsed && <span>Feed Plant Division</span>}
          </NavLink>
          <NavLink to="/culture-store/entry" data-testid="nav-stores-culture-store-entry"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <PlusCircle className="w-4 h-4" />
            {!collapsed && <span>Log Culture Store Entry</span>}
          </NavLink>
          <NavLink to="/culture-store" data-testid="nav-stores-culture-store"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <FlaskConical className="w-4 h-4" />
            {!collapsed && <span>Culture General Store</span>}
          </NavLink>
          <NavLink to="/kapl/entry" data-testid="nav-stores-kapl-entry"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <PlusCircle className="w-4 h-4" />
            {!collapsed && <span>Log KAPL Feed Entry</span>}
          </NavLink>
          <NavLink to="/kapl" data-testid="nav-stores-kapl"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <Warehouse className="w-4 h-4" />
            {!collapsed && <span>KAPL Feed Store</span>}
          </NavLink>
          <NavLink to="/rmfg/entry" data-testid="nav-stores-rmfg-entry"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <PlusCircle className="w-4 h-4" />
            {!collapsed && <span>Log RM & FG Entry</span>}
          </NavLink>
          <NavLink to="/rmfg" data-testid="nav-stores-rmfg"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <PackageOpen className="w-4 h-4" />
            {!collapsed && <span>RM & FG Inventory</span>}
          </NavLink>
          <NavLink to="/packing-store/entry" data-testid="nav-stores-packing-entry"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <PlusCircle className="w-4 h-4" />
            {!collapsed && <span>Log Packing Store Entry</span>}
          </NavLink>
          <NavLink to="/packing-store" data-testid="nav-stores-packing"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              isActive ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
            )}>
            <Package className="w-4 h-4" />
            {!collapsed && <span>Packing Store</span>}
          </NavLink>
        </nav>
        <div className="p-3 border-t border-dash-line">
          {!collapsed && <div className="text-xs text-dash-mut mb-2 truncate" title={user?.email}>{user?.email}</div>}
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} className="w-full justify-start" data-testid="stores-logout">
            <LogOut className="w-4 h-4 mr-2" /> {!collapsed && 'Sign out'}
          </Button>
        </div>
      </aside>
    );
  }

  return (
    <aside 
      className={cn(
        "h-screen bg-dash-bg border-r border-dash-line flex flex-col transition-all duration-300 flex-shrink-0 relative",
        collapsed ? "w-16" : "w-72"
      )}
      data-testid="sidebar"
    >
      {/* Floating collapse button on the border edge */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 z-50 w-6 h-6 rounded-full bg-dash-panel border border-dash-line flex items-center justify-center text-dash-mut hover:text-dash-ink hover:bg-dash-panel2 transition shadow-md cursor-pointer"
        style={{ top: '18px' }}
        data-testid="sidebar-toggle"
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
      </button>

      {/* Header */}
      <div className="p-4 border-b border-dash-line flex items-center justify-between">
        {!collapsed ? (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {organization?.logo_url ? (
              <img 
                src={organization.logo_url} 
                alt="Logo" 
                className="w-8 h-8 rounded object-cover flex-shrink-0"
              />
            ) : (
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center text-dash-ink text-sm font-bold bg-gradient-to-br from-dash-coral to-dash-ice flex-shrink-0"
              >
                {organization?.name?.charAt(0) || 'M'}
              </div>
            )}
            <span className="font-semibold text-dash-ink truncate">
              {organization?.name || 'SIL Insights'}
            </span>
          </div>
        ) : (
          <div className="mx-auto">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-dash-ink text-sm font-bold bg-gradient-to-br from-dash-coral to-dash-ice">
              {organization?.name?.charAt(0) || 'M'}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1">
        {/* Dashboards Section */}
        <div className="mb-2">
          <button
            onClick={() => !collapsed && setDashboardsExpanded(!dashboardsExpanded)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-dash-mut hover:bg-dash-panel hover:text-dash-ink transition-colors",
              collapsed && "justify-center"
            )}
          >
            <BarChart3 className="w-5 h-5 text-dash-ice flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="font-semibold flex-1 text-left">Dashboards</span>
                {dashboardsExpanded ? (
                  <ChevronUp className="w-4 h-4 text-dash-mut/60" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-dash-mut/60" />
                )}
              </>
            )}
          </button>

          {/* Dashboard List */}
          {!collapsed && dashboardsExpanded && (
            <div className="mt-1 ml-2 space-y-0.5 max-h-[50vh] overflow-y-auto pr-1">
              {/* Export Intelligence - always first */}
              <NavLink
                to="/intelligence"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-intelligence"
              >
                <Ship className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Export Intelligence</span>
              </NavLink>

              {/* Competitive Intelligence */}
              <NavLink
                to="/competitive"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-competitive"
              >
                <Users className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Competitive Intelligence</span>
              </NavLink>

              {/* Competitor Explorer */}
              <NavLink
                to="/explorer"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-explorer"
              >
                <Crosshair className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Competitor Explorer</span>
              </NavLink>

              {/* White-Space Ports */}
              <NavLink
                to="/whitespace"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-whitespace"
              >
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">White-Space Ports</span>
              </NavLink>

              {/* Market Drilldown */}
              <NavLink
                to="/drilldown"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-drilldown"
              >
                <Columns className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Market Drilldown</span>
              </NavLink>

              {/* Buyers Explorer */}
              <NavLink
                to="/buyers"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-buyers"
              >
                <Users className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Buyers Explorer</span>
              </NavLink>

              {/* Invoice & PI Tracker */}
              <NavLink
                to="/invoice-tracker"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-invoice-tracker"
              >
                <Receipt className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Invoice & PI Tracker</span>
              </NavLink>

              {/* Grade Strategy */}
              <NavLink
                to="/grade-strategy"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-grade-strategy"
              >
                <LineChart className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Grade Strategy</span>
              </NavLink>

              {/* Price Trend */}
              <NavLink
                to="/price-trend"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-price-trend"
              >
                <TrendingUp className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Price Trend</span>
              </NavLink>

              {/* Solar Project */}
              <NavLink
                to="/solar-project"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-solar-project"
              >
                <Sun className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Solar Project</span>
              </NavLink>

              {/* Market Watch — live Yahoo Finance quotes, refreshed every 30s */}
              <NavLink
                to="/market-watch"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-market-watch"
              >
                <CandlestickChart className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Market Watch</span>
              </NavLink>

              {/* General Stores — MRO inventory, FIFO cost & aging */}
              <NavLink
                to="/general-stores"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive 
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-general-stores"
              >
                <Package className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">General Stores</span>
              </NavLink>

              {/* Feed Plant Division — FIFO inventory for the feed plant */}
              <NavLink
                to="/feed-plant"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30"
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-feed-plant"
              >
                <Factory className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Feed Plant Division</span>
              </NavLink>

              {/* Culture General Store — Culture Section (chemicals, spares, general) */}
              <NavLink
                to="/culture-store"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30"
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-culture-store"
              >
                <FlaskConical className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Culture General Store</span>
              </NavLink>

              {/* KAPL — Feed Store closing stock (Vannastar variants) */}
              <NavLink
                to="/kapl"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30"
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-kapl"
              >
                <Warehouse className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">KAPL Feed Store</span>
              </NavLink>

              {/* Feed Plant RM & FG — raw materials + finished goods inventory */}
              <NavLink
                to="/rmfg"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30"
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-rmfg"
              >
                <PackageOpen className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">RM & FG Inventory</span>
              </NavLink>

              {/* Packing Store — cartons, pouches, labels, tapes FIFO inventory */}
              <NavLink
                to="/packing-store"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30"
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-packing-store"
              >
                <Package className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Packing Store</span>
              </NavLink>

              {/* SIL Export Pipeline — FY 2026-27 shipment planning */}
              <NavLink
                to="/sil-pipeline"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30"
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-sil-pipeline"
              >
                <Ship className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">SIL Export Pipeline</span>
              </NavLink>

              {/* Russian Federation Website Data — crustacean export register, admin-uploaded */}
              <NavLink
                to="/russia-register"
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive
                    ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30"
                    : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                )}
                data-testid="nav-russia-register"
              >
                <Globe className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Russian Federation Website Data</span>
              </NavLink>

              {/* Other dashboards */}
              {loading ? (
                <div className="px-3 py-2 text-dash-mut/60 text-sm">Loading...</div>
              ) : dashboards.length > 0 ? (
                dashboards.map((dashboard) => {
                  const Icon = getKPIIcon(dashboard.name);
                  return (
                    <NavLink
                      key={dashboard.id}
                      to={`/reports/${dashboard.id}`}
                      className={({ isActive }) => cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200",
                        isActive 
                          ? "bg-dash-panel2 text-dash-ice border border-dash-ice/30" 
                          : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
                      )}
                      data-testid={`nav-dashboard-${dashboard.id}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{dashboard.name.replace('KPI ', '').replace(': ', ' - ')}</span>
                    </NavLink>
                  );
                })
              ) : null}
            </div>
          )}

          {/* Collapsed state */}
          {collapsed && (
            <NavLink
              to="/intelligence"
              className={({ isActive }) => cn(
                "flex items-center justify-center px-3 py-2 rounded-lg transition-colors mt-1",
                isActive 
                  ? "bg-dash-panel2 text-dash-ice" 
                  : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink"
              )}
            >
              <Ship className="w-5 h-5" />
            </NavLink>
          )}
        </div>

        {/* Divider for Admin */}
        {isAdmin && !collapsed && (
          <div className="pt-2 pb-1">
            <div className="px-3 text-xs font-semibold text-dash-mut/60 uppercase tracking-wider">
              Admin Tools
            </div>
          </div>
        )}

        {/* Admin Navigation Items */}
        {filteredAdminItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
              isActive 
                ? "bg-dash-panel text-dash-ink" 
                : "text-dash-mut hover:bg-dash-panel hover:text-dash-ink",
              collapsed && "justify-center"
            )}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="font-medium">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-dash-line">
        {!collapsed && (
          <div className="mb-3">
            <p className="text-sm font-medium text-dash-ink truncate">{user?.name}</p>
            <p className="text-xs text-dash-mut/60 truncate">{user?.email}</p>
            <span className={cn(
              "inline-block mt-1 px-2 py-0.5 text-xs font-semibold tracking-wide uppercase rounded",
              user?.role === 'admin' 
                ? "bg-dash-coral/20 text-dash-coral" 
                : "bg-emerald-500/20 text-emerald-300"
            )}>
              {user?.role}
            </span>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className={cn(
            "w-full text-dash-mut hover:text-dash-ink hover:bg-dash-panel",
            collapsed && "px-2"
          )}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span className="ml-2">Logout</span>}
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
